import { useEffect, useRef, useState } from "react";
import { useNavigate } from "react-router-dom";
import { useForm, Controller } from "react-hook-form";
import { zodResolver } from "@hookform/resolvers/zod";
import { z } from "zod";
import { useQuery, useMutation, useQueryClient } from "@tanstack/react-query";
import {
  Upload,
  X,
  Tag as TagIcon,
  AlertCircle,
  AlertTriangle,
  ExternalLink,
  Loader2,
  CheckCircle2,
  FileText,
  FileSpreadsheet,
  Eye,
  EyeOff,
  BookOpen,
  Trash2,
  Lock,
} from "lucide-react";
import { api } from "@/api/client";
import ConfirmModal from "@/components/common/ConfirmModal";
import SearchableMultiSelect from "@/components/ui/SearchableMultiSelect";
import TaxonomyCascadePicker, {
  type SelectedNode as TaxonomySelectedNode,
} from "@/components/taxonomy/TaxonomyCascadePicker";
import type {
  JenisPNBP,
  SiklusKeuangan,
  BentukDokumen,
  Dimensi,
  Tag,
  KategoriInformasi,
  SubKategori,
  DocumentUpload,
  DocumentUploadResponse,
  ConfidentialityLevel,
} from "@/types/document";

// A candidate existing document returned by POST /documents/check-duplicate.
interface DuplicateMatch {
  id: string;
  judul: string;
  nomor_dokumen: string | null;
  file_name: string | null;
  status: string | null;
  created_at: string | null;
  match_reasons: ("file" | "nomor" | "judul")[];
  match_score: number;
  in_use: boolean;
  in_use_reasons: ("terbit" | "sitasi")[];
}

// ── Validation Schema ─────────────────────────────────────────────────────────

const uploadSchema = z.object({
  judul: z.string().min(3, "Judul minimal 3 karakter").max(500),
  deskripsi: z.string().optional(),
  nomor_dokumen: z.string().optional(),
  // Legacy & opsional: klasifikasi pakai taxonomy node; jenis diturunkan di server.
  jenis_pnbp_id: z.string().optional(),
  siklus_keuangan_id: z.string().uuid("Pilih siklus keuangan"),
  bentuk_dokumen_id: z.string().uuid("Pilih bentuk dokumen"),
  tingkat_kerahasiaan: z.enum(["PUBLIC", "INTERNAL", "CONFIDENTIAL", "SECRET"]),
  dimensi_ids: z.array(z.string().uuid()).default([]),
  tag_ids: z.array(z.string().uuid()).default([]),
  free_tags: z.array(z.string()).default([]),
  pic_ids: z.array(z.string().uuid()).default([]),
  stakeholder_ids: z.array(z.string().uuid()).default([]),
  unit_kerja_ids: z.array(z.string().uuid()).default([]),
  tanggal_dokumen: z.string().optional(),
  tanggal_berlaku: z.string().optional(),
  tanggal_berakhir: z.string().optional(),
  periode_retensi_tahun: z.number().min(1).max(100).optional(),
  kategori_informasi_id: z.string().optional(),
  sub_kategori_id: z.string().optional(),
  taxonomy_node_ids: z
    .array(z.string().uuid())
    .min(1, "Pilih minimal satu node Pohon Taxonomy PNBP")
    .default([]),
  primary_taxonomy_node_id: z.string().uuid().optional(),
});

type UploadFormValues = z.infer<typeof uploadSchema>;

// ── Props ─────────────────────────────────────────────────────────────────────

interface DocumentUploadFormProps {
  onSuccess?: (
    documentId: string,
    uploadResult?: DocumentUploadResponse,
  ) => void;
  onCancel?: () => void;
}

// ── File Preview ──────────────────────────────────────────────────────────────

function FilePreview({ file, url }: { file: File; url: string }) {
  const ext = file.name.split(".").pop()?.toLowerCase() ?? "";
  const isImage = ["jpg", "jpeg", "png", "webp"].includes(ext);
  const isPdf = ext === "pdf";

  return (
    <div className="mt-3 rounded-lg border border-gray-200 overflow-hidden">
      <div className="flex items-center justify-between px-3 py-2 bg-gray-50 border-b border-gray-200">
        <span className="text-xs font-medium text-gray-600">Preview</span>
        <span className="text-xs text-gray-400 truncate max-w-xs">
          {file.name}
        </span>
      </div>

      {isPdf && (
        <iframe
          src={url}
          title="PDF Preview"
          className="w-full"
          style={{ height: "480px" }}
        />
      )}

      {isImage && (
        <div className="flex items-center justify-center bg-gray-50 p-4">
          <img
            src={url}
            alt="Preview"
            className="max-h-80 max-w-full rounded object-contain"
          />
        </div>
      )}

      {!isPdf && !isImage && (
        <div className="flex flex-col items-center justify-center gap-2 py-10 text-gray-400">
          {ext === "xlsx" ? (
            <FileSpreadsheet className="h-10 w-10" />
          ) : (
            <FileText className="h-10 w-10" />
          )}
          <span className="text-sm">
            Preview tidak tersedia untuk format .{ext}
          </span>
        </div>
      )}
    </div>
  );
}

// ── Component ─────────────────────────────────────────────────────────────────

export default function DocumentUploadForm({
  onSuccess,
  onCancel,
}: DocumentUploadFormProps) {
  const [selectedStakeholderParentId, setSelectedStakeholderParentId] = useState<string>("");
  const [selectedFile, setSelectedFile] = useState<File | null>(null);
  const navigate = useNavigate();
  // Duplicate detection — hash from analyze-file, matches from check-duplicate.
  const analyzedHashRef = useRef<string | null>(null);
  const [duplicates, setDuplicates] = useState<DuplicateMatch[]>([]);
  // Duplicate targeted for deletion (drives confirm modal). null = closed.
  const [dupDeleteTarget, setDupDeleteTarget] = useState<DuplicateMatch | null>(null);
  const [previewUrl, setPreviewUrl] = useState<string | null>(null);
  const [showPreview, setShowPreview] = useState(false);
  const [freeTagInput, setFreeTagInput] = useState("");
  const [freeTags, setFreeTags] = useState<string[]>([]);
  const [showDeskripsiModal, setShowDeskripsiModal] = useState(false);
  const [dragOver, setDragOver] = useState(false);
  const [aiStatus, setAiStatus] = useState<
    "idle" | "analyzing" | "done" | "partial" | "error"
  >("idle");
  const [aiError, setAiError] = useState("");
  const pollTimerRef = useRef<ReturnType<typeof setTimeout> | null>(null);
  const queryClient = useQueryClient();

  // Create / revoke object URL for file preview
  useEffect(() => {
    if (!selectedFile) {
      setPreviewUrl(null);
      return;
    }
    const url = URL.createObjectURL(selectedFile);
    setPreviewUrl(url);
    return () => URL.revokeObjectURL(url);
  }, [selectedFile]);

  const {
    register,
    control,
    handleSubmit,
    watch,
    setValue,
    reset,
    formState: { errors, isSubmitting },
  } = useForm<UploadFormValues>({
    resolver: zodResolver(uploadSchema),
    defaultValues: {
      tingkat_kerahasiaan: "INTERNAL",
      dimensi_ids: [],
      tag_ids: [],
      free_tags: [],
      pic_ids: [],
      stakeholder_ids: [],
      unit_kerja_ids: [],
      kategori_informasi_id: "",
      sub_kategori_id: "",
      taxonomy_node_ids: [],
      primary_taxonomy_node_id: undefined,
    },
  });

  // Taxonomy nodes state (UI side) — m2m dengan tag is_primary.
  // Derivasi Jenis PNBP otomatis dipindah ke bawah setelah jenisList tersedia.
  const [taxonomySelected, setTaxonomySelected] = useState<TaxonomySelectedNode[]>([]);

  const watchedKategoriId = watch("kategori_informasi_id");
  const watchedDeskripsi = watch("deskripsi");
  const watchedBentukId = watch("bentuk_dokumen_id");

  // ── Master data queries ─────────────────────────────────────────────────────
  const { data: jenisList = [] } = useQuery<JenisPNBP[]>({
    queryKey: ["master", "jenis-pnbp"],
    queryFn: () => api.get("/master/jenis-pnbp"),
  });

  // Derive jenis_pnbp_id from the primary taxonomy node's Jenis-level kode.
  // Konvensi: node Jenis PNBP (anak langsung root "PNBP") berkode tunggal "N"
  // → jenis kode "J0N". Cari kode numerik-tunggal di rantai (lewati root "PNBP"
  // dan level lebih dalam seperti "1.1"), supaya tetap benar baik root
  // disertakan (skipRoot=false) maupun tidak.
  const derivedJenis = (() => {
    const primary = taxonomySelected.find((t) => t.is_primary) ?? taxonomySelected[0];
    if (!primary) return null;
    const jenisKodeRaw = primary.kodes.find((k) => /^\d+$/.test(k));
    // Ada kode Jenis numerik ("1".."6") → J01..J06. Kalau berhenti di root
    // "PNBP" (tanpa Jenis), pakai J00 "PNBP (Umum)".
    const jenisKode = jenisKodeRaw
      ? `J${jenisKodeRaw.padStart(2, "0")}`
      : primary.kodes.includes("PNBP")
        ? "J00"
        : null;
    if (!jenisKode) return null;
    return jenisList.find((j) => j.kode === jenisKode) ?? null;
  })();

  // Ref selalu menunjuk taxonomySelected terbaru — dipakai AI auto-fill (yang
  // jalan async) untuk cek apakah user sudah memilih taxonomy, tanpa closure basi.
  const taxonomySelectedRef = useRef(taxonomySelected);
  useEffect(() => {
    taxonomySelectedRef.current = taxonomySelected;
    const ids = taxonomySelected.map((t) => t.id);
    const primary = taxonomySelected.find((t) => t.is_primary)?.id;
    setValue("taxonomy_node_ids", ids);
    setValue("primary_taxonomy_node_id", primary);
    // Hanya sinkronkan jenis_pnbp_id kalau cascade non-kosong — biar AI
    // auto-fill (yang juga setValue jenis_pnbp_id) tidak ter-clobber pada
    // initial mount sebelum user menyentuh cascade.
    if (taxonomySelected.length > 0) {
      setValue("jenis_pnbp_id", derivedJenis?.id ?? "", { shouldValidate: !!derivedJenis });
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [taxonomySelected, derivedJenis?.id]);

  const { data: siklusList = [] } = useQuery<SiklusKeuangan[]>({
    queryKey: ["master", "siklus-keuangan"],
    queryFn: () => api.get("/master/siklus-keuangan"),
  });

  const { data: bentukList = [] } = useQuery<BentukDokumen[]>({
    queryKey: ["master", "bentuk-dokumen"],
    queryFn: () => api.get("/master/bentuk-dokumen"),
  });

  const { data: dimensiList = [] } = useQuery<Dimensi[]>({
    queryKey: ["master", "dimensi"],
    queryFn: () => api.get("/master/dimensi"),
  });

  const { data: tagList = [] } = useQuery<Tag[]>({
    queryKey: ["tags"],
    queryFn: () => api.get("/tags/"),
  });

  const { data: picList = [] } = useQuery<
    { id: string; nama: string; jabatan?: string }[]
  >({
    queryKey: ["master", "pic"],
    queryFn: () => api.get("/master/pic"),
  });

  const { data: stakeholderList = [] } = useQuery<
    { id: string; nama: string; tipe?: string; parent_id?: string | null }[]
  >({
    queryKey: ["master", "stakeholder"],
    queryFn: () => api.get("/master/stakeholder"),
  });

  const { data: unitKerjaList = [] } = useQuery<
    { id: string; kode: string; nama: string }[]
  >({
    queryKey: ["master", "unit-kerja"],
    queryFn: () => api.get("/master/unit-kerja"),
  });

  const { data: kategoriList = [] } = useQuery<KategoriInformasi[]>({
    queryKey: ["master", "kategori-informasi"],
    queryFn: () => api.get("/master/kategori-informasi"),
  });

  const { data: subKategoriList = [] } = useQuery<SubKategori[]>({
    queryKey: ["master", "sub-kategori", watchedKategoriId],
    queryFn: () =>
      api.get("/master/sub-kategori", { kategori_id: watchedKategoriId }),
    enabled: !!watchedKategoriId,
  });

  // Rule hak akses otomatis (MoM A1): bentuk dokumen REGULASI (legal) → PUBLIC
  // (umum), DOKUMEN_PENDUKUNG (non-legal) → INTERNAL. Diterapkan sebagai default
  // saat bentuk dipilih/di-isi AI; pengguna tetap bisa override manual setelahnya.
  useEffect(() => {
    if (!watchedBentukId) return;
    const bentuk = bentukList.find((b) => b.id === watchedBentukId);
    if (!bentuk?.jenis_repositori) return;
    setValue(
      "tingkat_kerahasiaan",
      bentuk.jenis_repositori === "LEGAL" ? "PUBLIC" : "INTERNAL",
    );
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [watchedBentukId, bentukList]);

  // ── Submit mutation ─────────────────────────────────────────────────────────
  const createMutation = useMutation({
    mutationFn: async (data: UploadFormValues & { free_tags?: string[] }) => {
      // Build payload — omit empty date strings entirely so Pydantic
      // treats them as None (the field is Optional[datetime]).
      // eslint-disable-next-line @typescript-eslint/no-explicit-any
      const payload: Record<string, any> = {
        ...data,
        free_tags: data.free_tags ?? freeTags,
      };
      if (!payload.tanggal_dokumen) delete payload.tanggal_dokumen;
      if (!payload.tanggal_berlaku) delete payload.tanggal_berlaku;
      if (!payload.tanggal_berakhir) delete payload.tanggal_berakhir;
      if (!payload.kategori_informasi_id) delete payload.kategori_informasi_id;
      if (!payload.sub_kategori_id) delete payload.sub_kategori_id;
      if (!payload.primary_taxonomy_node_id) delete payload.primary_taxonomy_node_id;
      // jenis_pnbp_id legacy: diturunkan dari taxonomy node di server. Jangan kirim
      // string kosong (Pydantic Optional[UUID] menolaknya).
      if (!payload.jenis_pnbp_id) delete payload.jenis_pnbp_id;
      return await api.post<{ id: string }>("/documents/", payload);
    },
    onSuccess: async (doc) => {
      let uploadResult: DocumentUploadResponse | undefined;
      if (selectedFile) {
        const formData = new FormData();
        formData.append("file", selectedFile);
        uploadResult = await api.upload<DocumentUploadResponse>(
          `/documents/${doc.id}/upload`,
          formData,
          { timeout: 300000 },
        );
      }
      await queryClient.invalidateQueries({ queryKey: ["documents"] });
      onSuccess?.(doc.id, uploadResult);
    },
  });

  // Delete an existing (duplicate) document straight from the warning banner.
  // Only enabled for documents that are NOT in use (see in_use flag).
  const deleteDuplicateMutation = useMutation({
    mutationFn: (id: string) => api.delete(`/documents/${id}`),
    onSuccess: (_data, id) => {
      setDuplicates((prev) => prev.filter((d) => d.id !== id));
      setDupDeleteTarget(null);
      queryClient.invalidateQueries({ queryKey: ["documents"] });
    },
  });

  // ── File drag-and-drop ──────────────────────────────────────────────────────
  function handleFileDrop(e: React.DragEvent) {
    e.preventDefault();
    setDragOver(false);
    const file = e.dataTransfer.files[0];
    if (file) handleFileSelected(file);
  }

  function handleFileInput(e: React.ChangeEvent<HTMLInputElement>) {
    const file = e.target.files?.[0];
    if (file) handleFileSelected(file);
  }

  const ALLOWED_EXTS = ["pdf", "doc", "docx"];
  const MAX_SIZE_BYTES = 25 * 1024 * 1024;

  function validateFile(file: File): string | null {
    const ext = file.name.split(".").pop()?.toLowerCase() ?? "";
    if (!ALLOWED_EXTS.includes(ext))
      return `Format tidak didukung (.${ext}). Hanya PDF dan Word (.doc, .docx) yang diizinkan.`;
    if (file.size > MAX_SIZE_BYTES)
      return `Ukuran file terlalu besar (${(file.size / 1024 / 1024).toFixed(1)} MB). Maksimal 25 MB.`;
    return null;
  }

  function handleFileSelected(file: File) {
    const err = validateFile(file);
    if (err) {
      setAiStatus("error");
      setAiError(err);
      return;
    }
    setSelectedFile(file);
    analyzeFile(file);
  }

  async function analyzeFile(file: File) {
    setAiStatus("analyzing");
    setAiError("");
    setDuplicates([]);
    analyzedHashRef.current = null;
    try {
      const formData = new FormData();
      formData.append("file", file);
      const { job_id, file_hash } = await api.upload<{
        job_id: string;
        status: string;
        file_hash?: string;
      }>("/documents/analyze-file", formData, { timeout: 900_000 });
      analyzedHashRef.current = file_hash ?? null;
      // Early exact-content check (by hash) while the AI metadata is still being
      // extracted; the nomor/judul check runs again once metadata arrives.
      void runDuplicateCheck();
      schedulePoll(job_id, 0);
    } catch {
      setAiStatus("error");
      setAiError(
        "Pengisian otomatis tidak dapat diselesaikan. Silakan lengkapi data secara manual.",
      );
    }
  }

  // Ask the backend whether a document like this already exists (by file hash,
  // nomor dokumen, or similar judul). Best-effort — failures are silent.
  async function runDuplicateCheck(judul?: string | null, nomor?: string | null) {
    const fileHash = analyzedHashRef.current;
    if (!fileHash && !judul && !nomor) return;
    try {
      const res = await api.post<{ matches: DuplicateMatch[] }>(
        "/documents/check-duplicate",
        {
          file_hash: fileHash ?? undefined,
          judul: judul ?? undefined,
          nomor_dokumen: nomor ?? undefined,
        },
      );
      setDuplicates(res.matches ?? []);
    } catch {
      /* dedup check is advisory; ignore errors */
    }
  }

  function schedulePoll(jobId: string, attempts: number) {
    pollTimerRef.current = setTimeout(() => pollJob(jobId, attempts), 3000);
  }

  async function pollJob(jobId: string, attempts: number) {
    if (attempts > 400) {
      // ~20 menit. Analisis = 4 panggilan LLM berurutan (metadata, klasifikasi,
      // ringkasan, stakeholder); pada LLM lambat (Ollama remote) tiap panggilan
      // bisa ~150-250 dtk. Job jalan di background (Celery), jadi beri waktu lega.
      setAiStatus("error");
      setAiError(
        "Proses analisis memakan waktu terlalu lama. Silakan coba lagi.",
      );
      return;
    }
    try {
      const res = await api.get<{
        status: string;
        error?: string;
        result?: {
          success: boolean;
          error?: string;
          page_count?: number;
          metadata?: {
            judul?: string;
            nomor_dokumen?: string | null;
            tanggal_dokumen?: string | null;
            tanggal_berlaku?: string | null;
            tanggal_berakhir?: string | null;
          };
          classification?: {
            jenis_pnbp?: string;
            siklus?: string;
            bentuk?: string;
            dimensi?: string[];
            tags?: string[];
            confidence?: number;
            alasan?: string;
          };
          summary?: string;
          stakeholder_ids?: string[];
          taxonomy?: {
            node_id: string;
            kode?: string | null;
            label?: string;
            kodes?: string[];
            confidence?: number | null;
            alasan?: string | null;
          } | null;
        };
      }>(`/documents/analyze-jobs/${jobId}`);

      if (res.status === "processing") {
        schedulePoll(jobId, attempts + 1);
        return;
      }

      if (res.status === "done" && res.result) {
        applyAiResult(res.result);
      } else {
        setAiStatus("error");
        setAiError(
          res.error ??
            "Pengisian otomatis tidak dapat diselesaikan. Silakan lengkapi data secara manual.",
        );
      }
    } catch {
      setAiStatus("error");
      setAiError(
        "Pengisian otomatis tidak dapat diselesaikan. Silakan lengkapi data secara manual.",
      );
    }
  }

  function applyAiResult(result: {
    metadata?: {
      judul?: string;
      nomor_dokumen?: string | null;
      tanggal_dokumen?: string | null;
      tanggal_berlaku?: string | null;
      tanggal_berakhir?: string | null;
    };
    classification?: {
      jenis_pnbp?: string;
      siklus?: string;
      bentuk?: string;
      dimensi?: string[];
      tags?: string[];
    };
    summary?: string;
    stakeholder_ids?: string[];
    taxonomy?: {
      node_id: string;
      kode?: string | null;
      label?: string;
      kodes?: string[];
      confidence?: number | null;
      alasan?: string | null;
    } | null;
  }) {
    let filled = 0;
    let expected = 0;

    const m = result.metadata;
    expected += 1;
    if (m) {
      if (m.judul) {
        setValue("judul", m.judul);
        filled += 1;
      }
      const validDate = (v?: string | null) =>
        v && v !== "null" && v !== "None" ? v : undefined;
      if (m.nomor_dokumen && m.nomor_dokumen !== "null")
        setValue("nomor_dokumen", m.nomor_dokumen);
      const td = validDate(m.tanggal_dokumen);
      const tb = validDate(m.tanggal_berlaku);
      const tbe = validDate(m.tanggal_berakhir);
      if (td) setValue("tanggal_dokumen", td);
      if (tb) setValue("tanggal_berlaku", tb);
      if (tbe) setValue("tanggal_berakhir", tbe);
    }

    const c = result.classification;
    expected += 3;
    if (c) {
      // Jenis PNBP otoritatif di-derive dari Pohon Taxonomy. Tebakan AI hanya
      // mengisi kalau user BELUM memilih node taxonomy apa pun — supaya tidak
      // menimpa pilihan user (mis. root PNBP → J00) saat analisis AI selesai
      // setelah user memilih (race).
      if (c.jenis_pnbp && taxonomySelectedRef.current.length === 0) {
        const match = jenisList.find(
          (j) => j.kode.toLowerCase() === c.jenis_pnbp!.toLowerCase(),
        );
        if (match) {
          setValue("jenis_pnbp_id", match.id);
          filled += 1;
        }
      }
      if (c.siklus) {
        const match = siklusList.find(
          (s) => s.kode.toLowerCase() === c.siklus!.toLowerCase(),
        );
        if (match) {
          setValue("siklus_keuangan_id", match.id);
          filled += 1;
        }
      }
      if (c.bentuk) {
        const match = bentukList.find(
          (b) => b.kode.toLowerCase() === c.bentuk!.toLowerCase(),
        );
        if (match) {
          setValue("bentuk_dokumen_id", match.id);
          filled += 1;
        }
      }
      if (c.dimensi && c.dimensi.length > 0) {
        const matchedIds = c.dimensi
          .map((code) =>
            dimensiList.find(
              (d) => d.kode.toLowerCase() === code.toLowerCase(),
            ),
          )
          .filter(Boolean)
          .map((d) => d!.id);
        if (matchedIds.length > 0) setValue("dimensi_ids", matchedIds);
      }
      if (c.tags && c.tags.length > 0) {
        const structuredIds: string[] = [];
        const newFreeTags: string[] = [];
        for (const tagName of c.tags) {
          const match = tagList.find(
            (t) => t.nama.toLowerCase() === tagName.toLowerCase(),
          );
          if (match) {
            structuredIds.push(match.id);
          } else {
            newFreeTags.push(tagName);
          }
        }
        if (structuredIds.length > 0) {
          const currentTagIds: string[] = (watch("tag_ids") as string[]) ?? [];
          setValue("tag_ids", [
            ...new Set([...currentTagIds, ...structuredIds]),
          ]);
        }
        if (newFreeTags.length > 0) {
          setFreeTags((prev) => [...new Set([...prev, ...newFreeTags])]);
        }
      }
    }

    // Pohon Taxonomy PNBP — terapkan node yang dipilih AI ke picker, HANYA kalau
    // user belum memilih node apa pun (sama seperti guard jenis_pnbp), supaya tidak
    // menimpa pilihan manual saat analisis async selesai. Mengisi taxonomySelected
    // memicu useEffect yang men-set taxonomy_node_ids, primary, & menurunkan Jenis
    // PNBP otomatis dari rantai kode node.
    const tx = result.taxonomy;
    if (tx?.node_id && taxonomySelectedRef.current.length === 0) {
      expected += 1;
      setTaxonomySelected([
        {
          id: tx.node_id,
          label: tx.label ?? tx.kode ?? "",
          is_primary: true,
          kodes: tx.kodes ?? [],
        },
      ]);
      filled += 1;
    }

    if (result.stakeholder_ids && result.stakeholder_ids.length > 0) {
      setValue("stakeholder_ids", [result.stakeholder_ids[0]]);
    }
    if (result.summary) {
      setValue("deskripsi", result.summary);
    }

    if (filled === 0) {
      setAiStatus("error");
      setAiError(
        "Pengisian otomatis tidak dapat diselesaikan. Silakan lengkapi data secara manual.",
      );
    } else if (filled < expected) {
      setAiStatus("partial");
    } else {
      setAiStatus("done");
    }

    // Re-check for existing documents now that judul & nomor are known (covers
    // same-document re-uploads whose bytes differ — e.g. re-scanned PDFs).
    void runDuplicateCheck(m?.judul, m?.nomor_dokumen);
  }

  function addFreeTag() {
    const trimmed = freeTagInput.trim();
    if (trimmed && !freeTags.includes(trimmed)) {
      setFreeTags([...freeTags, trimmed]);
    }
    setFreeTagInput("");
  }

  function removeFreeTag(tag: string) {
    setFreeTags(freeTags.filter((t) => t !== tag));
  }

  const onSubmit = (data: UploadFormValues) => {
    const pending = freeTagInput.trim();
    const merged =
      pending && !freeTags.includes(pending)
        ? [...freeTags, pending]
        : freeTags;
    setFreeTags(merged);
    setFreeTagInput("");
    createMutation.mutate({ ...data, free_tags: merged });
  };

  return (
    <form onSubmit={handleSubmit(onSubmit)} className="space-y-6">
      {createMutation.isError && (
        <div className="flex items-center gap-2 rounded-lg bg-red-50 border border-red-200 p-4 text-red-700">
          <AlertCircle className="h-5 w-5 flex-shrink-0" />
          <span className="text-sm">
            Gagal menyimpan dokumen. Silakan coba lagi.
          </span>
        </div>
      )}

      {/* ── File Upload (FIRST — so AI can pre-fill fields below) ── */}
      <div>
        <label className="block text-sm font-medium text-gray-700 mb-1">
          Upload Dokumen
        </label>
        <div
          className={`relative flex flex-col items-center justify-center rounded-lg border-2 border-dashed p-6 transition-colors ${
            dragOver
              ? "border-blue-400 bg-blue-50"
              : "border-gray-300 hover:border-gray-400"
          }`}
          onDragOver={(e) => {
            e.preventDefault();
            setDragOver(true);
          }}
          onDragLeave={() => setDragOver(false)}
          onDrop={handleFileDrop}
        >
          <Upload className="h-8 w-8 text-gray-400 mb-2" />
          {selectedFile ? (
            <div className="text-center">
              <p className="text-sm font-medium text-gray-700">
                {selectedFile.name}
              </p>
              <p className="text-xs text-gray-500">
                {(selectedFile.size / 1024 / 1024).toFixed(2)} MB
              </p>
              <div className="mt-2 flex items-center justify-center gap-3">
                {previewUrl && (
                  <button
                    type="button"
                    onClick={() => setShowPreview((v) => !v)}
                    className="flex items-center gap-1 text-xs text-blue-600 hover:text-blue-700"
                  >
                    {showPreview ? (
                      <>
                        <EyeOff className="h-3.5 w-3.5" /> Tutup preview
                      </>
                    ) : (
                      <>
                        <Eye className="h-3.5 w-3.5" /> Lihat file
                      </>
                    )}
                  </button>
                )}
                <button
                  type="button"
                  onClick={() => {
                    if (pollTimerRef.current)
                      clearTimeout(pollTimerRef.current);
                    setSelectedFile(null);
                    setShowPreview(false);
                    setAiStatus("idle");
                    setAiError("");
                    setDuplicates([]);
                    analyzedHashRef.current = null;
                    setFreeTags([]);
                    setFreeTagInput("");
                    reset({
                      judul: "",
                      deskripsi: "",
                      nomor_dokumen: "",
                      jenis_pnbp_id: "",
                      siklus_keuangan_id: "",
                      bentuk_dokumen_id: "",
                      tingkat_kerahasiaan: "INTERNAL",
                      dimensi_ids: [],
                      tag_ids: [],
                      free_tags: [],
                      pic_ids: [],
                      stakeholder_ids: [],
                      unit_kerja_ids: [],
                      tanggal_dokumen: "",
                      tanggal_berlaku: "",
                      tanggal_berakhir: "",
                      periode_retensi_tahun: undefined,
                      kategori_informasi_id: "",
                      sub_kategori_id: "",
                      taxonomy_node_ids: [],
                      primary_taxonomy_node_id: undefined,
                    });
                  }}
                  className="text-xs text-red-500 hover:text-red-700"
                >
                  Hapus file
                </button>
              </div>
            </div>
          ) : (
            <div className="text-center">
              <p className="text-sm text-gray-600">
                Seret file ke sini atau{" "}
                <label className="cursor-pointer text-blue-600 hover:text-blue-500">
                  pilih file
                  <input
                    type="file"
                    className="hidden"
                    accept=".pdf,.doc,.docx"
                    onChange={handleFileInput}
                  />
                </label>
              </p>
              <p className="text-xs text-gray-400 mt-1">
                PDF, DOC, DOCX — maks. 25 MB
              </p>
            </div>
          )}
        </div>

        {/* AI Analysis Status */}
        {aiStatus === "analyzing" && (
          <div className="mt-3 flex items-center gap-2 rounded-lg bg-blue-50 border border-blue-200 px-4 py-3 text-blue-700">
            <Loader2 className="h-4 w-4 animate-spin flex-shrink-0" />
            <span className="text-sm">
              Formulir sedang diisi otomatis oleh AI dari dokumen. Proses ini bisa
              memakan beberapa menit (tergantung kecepatan server AI) — silakan
              menunggu, jangan tutup halaman ini.
            </span>
          </div>
        )}
        {aiStatus === "done" && (
          <div className="mt-3 flex items-center gap-2 rounded-lg bg-emerald-50 border border-emerald-200 px-4 py-3 text-emerald-700">
            <CheckCircle2 className="h-4 w-4 flex-shrink-0" />
            <span className="text-sm">
              Formulir telah terisi otomatis. Silakan tinjau dan lakukan
              penyesuaian jika diperlukan.
            </span>
          </div>
        )}
        {aiStatus === "partial" && (
          <div className="mt-3 flex items-center gap-2 rounded-lg bg-amber-50 border border-amber-200 px-4 py-3 text-amber-700">
            <AlertCircle className="h-4 w-4 flex-shrink-0" />
            <span className="text-sm">
              Sebagian data tidak dapat diisi otomatis. Mohon lengkapi bagian
              yang tersedia.
            </span>
          </div>
        )}
        {aiStatus === "error" && (
          <div className="mt-3 flex items-center gap-2 rounded-lg bg-red-50 border border-red-200 px-4 py-3 text-red-700">
            <AlertCircle className="h-4 w-4 flex-shrink-0" />
            <span className="text-sm">{aiError}</span>
          </div>
        )}

        {/* ── Duplicate warning ── */}
        {duplicates.length > 0 && (
          <div className="mt-3 rounded-lg border border-amber-300 bg-amber-50 px-4 py-3">
            <div className="flex items-start gap-2 text-amber-800">
              <AlertTriangle className="mt-0.5 h-4 w-4 flex-shrink-0" />
              <div className="text-sm">
                <p className="font-semibold">
                  Dokumen serupa sudah ada di sistem ({duplicates.length})
                </p>
                <p className="mt-0.5 text-amber-700">
                  Anda tetap dapat menyimpan, namun pertimbangkan menambahkannya
                  sebagai <strong>versi baru</strong> pada dokumen yang sudah ada
                  agar tidak terjadi duplikasi.
                </p>
              </div>
            </div>
            <ul className="mt-3 space-y-2">
              {duplicates.slice(0, 5).map((d) => (
                <li
                  key={d.id}
                  className="flex flex-wrap items-center justify-between gap-2 rounded-md border border-amber-200 bg-white px-3 py-2"
                >
                  <div className="min-w-0">
                    <div className="truncate text-sm font-medium text-slate-800">
                      {d.judul}
                    </div>
                    <div className="mt-0.5 flex flex-wrap items-center gap-1.5 text-xs text-slate-500">
                      {d.nomor_dokumen && <span>{d.nomor_dokumen}</span>}
                      {d.status && (
                        <span className="rounded bg-slate-100 px-1.5 py-0.5">
                          {d.status}
                        </span>
                      )}
                      {d.match_reasons.map((r) => (
                        <span
                          key={r}
                          className="rounded bg-amber-100 px-1.5 py-0.5 text-amber-800"
                        >
                          {r === "file"
                            ? "Isi file identik"
                            : r === "nomor"
                              ? "Nomor sama"
                              : "Judul mirip"}
                        </span>
                      ))}
                      {d.in_use && (
                        <span
                          className="inline-flex items-center gap-1 rounded bg-emerald-100 px-1.5 py-0.5 font-medium text-emerald-700"
                          title={
                            "Dokumen sedang dipakai" +
                            (d.in_use_reasons.includes("sitasi")
                              ? " — dikutip di konten taksonomi"
                              : "") +
                            (d.in_use_reasons.includes("terbit")
                              ? " — sudah diterbitkan"
                              : "")
                          }
                        >
                          <Lock className="h-3 w-3" />
                          Sedang dipakai
                          {d.in_use_reasons.includes("sitasi") ? " · sitasi" : ""}
                          {d.in_use_reasons.includes("terbit") ? " · terbit" : ""}
                        </span>
                      )}
                    </div>
                  </div>
                  <div className="flex items-center gap-2">
                    <a
                      href={`/documents/${d.id}`}
                      target="_blank"
                      rel="noopener noreferrer"
                      className="inline-flex items-center gap-1 rounded-md border border-slate-300 px-2.5 py-1.5 text-xs font-medium text-slate-600 transition hover:bg-slate-50"
                    >
                      <ExternalLink className="h-3.5 w-3.5" /> Lihat
                    </a>
                    <button
                      type="button"
                      onClick={() => navigate(`/documents/${d.id}`)}
                      className="inline-flex items-center gap-1 rounded-md bg-amber-600 px-2.5 py-1.5 text-xs font-semibold text-white transition hover:bg-amber-700"
                    >
                      Tambah versi baru
                    </button>
                    {d.in_use ? (
                      <span
                        className="inline-flex cursor-not-allowed items-center gap-1 rounded-md border border-slate-200 px-2.5 py-1.5 text-xs font-medium text-slate-300"
                        title="Tidak dapat dihapus: dokumen sedang dipakai"
                      >
                        <Trash2 className="h-3.5 w-3.5" /> Hapus
                      </span>
                    ) : (
                      <button
                        type="button"
                        onClick={() => setDupDeleteTarget(d)}
                        className="inline-flex items-center gap-1 rounded-md px-2.5 py-1.5 text-xs font-medium text-red-600 transition hover:bg-red-50"
                        title="Hapus dokumen duplikat ini"
                      >
                        <Trash2 className="h-3.5 w-3.5" /> Hapus
                      </button>
                    )}
                  </div>
                </li>
              ))}
            </ul>
            {duplicates.length > 5 && (
              <p className="mt-2 text-xs text-amber-700">
                +{duplicates.length - 5} dokumen serupa lainnya.
              </p>
            )}
          </div>
        )}

        {/* ── File Preview ── */}
        {showPreview && selectedFile && previewUrl && (
          <FilePreview file={selectedFile} url={previewUrl} />
        )}
      </div>

      {/* ── Judul ── */}
      <div>
        <label className="block text-sm font-medium text-gray-700 mb-1">
          Judul Dokumen <span className="text-red-500">*</span>
        </label>
        <input
          {...register("judul")}
          className="w-full rounded-lg border border-gray-300 px-3 py-2 text-sm focus:border-blue-500 focus:ring-1 focus:ring-blue-500"
          placeholder="Masukkan judul dokumen..."
        />
        {errors.judul && (
          <p className="mt-1 text-xs text-red-600">{errors.judul.message}</p>
        )}
      </div>

      {/* ── Nomor Dokumen ── */}
      <div>
        <label className="block text-sm font-medium text-gray-700 mb-1">
          Nomor Dokumen
        </label>
        <input
          {...register("nomor_dokumen")}
          className="w-full rounded-lg border border-gray-300 px-3 py-2 text-sm"
          placeholder="Contoh: SK-2025-001"
        />
      </div>

      {/* ── Kategori Informasi & Sub-Kategori ── */}
      <div className="grid grid-cols-1 gap-4 sm:grid-cols-2">
        <div>
          <label className="block text-sm font-medium text-gray-700 mb-1">
            Kategori Informasi
          </label>
          <select
            {...register("kategori_informasi_id", {
              onChange: () => setValue("sub_kategori_id", ""),
            })}
            className="w-full rounded-lg border border-gray-300 px-3 py-2 text-sm focus:border-blue-500 focus:ring-1 focus:ring-blue-500"
          >
            <option value="">Pilih Kategori</option>
            {kategoriList.map((k) => (
              <option key={k.id} value={k.id}>
                {k.kode.replace("K", "")}. {k.nama}
              </option>
            ))}
          </select>
        </div>

        <div>
          <label className="block text-sm font-medium text-gray-700 mb-1">
            Sub-Kategori
          </label>
          <select
            {...register("sub_kategori_id")}
            disabled={!watchedKategoriId}
            className="w-full rounded-lg border border-gray-300 px-3 py-2 text-sm focus:border-blue-500 focus:ring-1 focus:ring-blue-500 disabled:bg-gray-50 disabled:text-gray-400 disabled:cursor-not-allowed"
          >
            <option value="">
              {watchedKategoriId
                ? "Pilih Sub-Kategori"
                : "Pilih Kategori terlebih dahulu"}
            </option>
            {subKategoriList.map((sk) => (
              <option key={sk.id} value={sk.id}>
                {sk.nama}
              </option>
            ))}
          </select>
        </div>
      </div>

      {/* ── Taxonomy Domain Picker (SDA → Minerba → Logam → Nikel → Bijih Nikel) ── */}
      <div>
        <label className="block text-sm font-medium text-gray-700 mb-1">
          Pohon Taxonomy PNBP <span className="text-red-500">*</span>
          <span className="ml-2 text-xs font-normal text-gray-500">
            (mulai dari root PNBP lalu Jenis PNBP; boleh berhenti di level mana saja — tidak harus sampai paling spesifik. Bintang ⭐ menandai primer)
          </span>
        </label>
        <TaxonomyCascadePicker
          value={taxonomySelected}
          onChange={setTaxonomySelected}
          taxonomyType="PNBP_DOMAIN"
          leafOnly={false}
        />
        {(() => {
          const primary =
            taxonomySelected.find((t) => t.is_primary) ?? taxonomySelected[0];
          if (!primary)
            return (
              <p className="mt-2 text-xs text-amber-600">
                Wajib pilih minimal satu node Pohon Taxonomy PNBP.
              </p>
            );
          const leaf = primary.label.split(" / ").pop();
          return (
            <p className="mt-2 text-xs text-emerald-700">
              ✓ Klasifikasi: <strong>{leaf}</strong>
              <span className="text-emerald-600/80"> — {primary.label}</span>
            </p>
          );
        })()}
        {errors.taxonomy_node_ids && (
          <p className="mt-1 text-xs text-red-600">
            {errors.taxonomy_node_ids.message}
          </p>
        )}
      </div>

      {/* ── Classification Row (Siklus & Bentuk; Jenis di-derive dari taxonomy) ── */}
      <div className="grid grid-cols-1 gap-4 sm:grid-cols-2">
        {/* Siklus Keuangan */}
        <div>
          <label className="block text-sm font-medium text-gray-700 mb-1">
            Siklus Keuangan <span className="text-red-500">*</span>
          </label>
          <select
            {...register("siklus_keuangan_id")}
            className="w-full rounded-lg border border-gray-300 px-3 py-2 text-sm"
          >
            <option value="">Pilih Siklus</option>
            {siklusList.map((s) => (
              <option key={s.id} value={s.id}>
                {s.kode} — {s.nama}
              </option>
            ))}
          </select>
          {errors.siklus_keuangan_id && (
            <p className="mt-1 text-xs text-red-600">
              {errors.siklus_keuangan_id.message}
            </p>
          )}
        </div>

        {/* Bentuk Dokumen */}
        <div>
          <label className="block text-sm font-medium text-gray-700 mb-1">
            Bentuk Dokumen <span className="text-red-500">*</span>
          </label>
          <select
            {...register("bentuk_dokumen_id")}
            className="w-full rounded-lg border border-gray-300 px-3 py-2 text-sm"
          >
            <option value="">Pilih Bentuk</option>
            {bentukList.map((b) => (
              <option key={b.id} value={b.id}>
                {b.kode} — {b.nama}
              </option>
            ))}
          </select>
          {(() => {
            const sel = bentukList.find((b) => b.id === watchedBentukId);
            if (!sel?.jenis_repositori) return null;
            const legal = sel.jenis_repositori === "LEGAL";
            return (
              <p className="mt-1.5 flex items-center gap-1.5 text-xs">
                <span
                  className={`rounded-full px-2 py-0.5 font-semibold ${
                    legal ? "bg-emerald-100 text-emerald-700" : "bg-slate-100 text-slate-600"
                  }`}
                >
                  {legal ? "DOKUMEN LEGAL" : "NON-LEGAL"}
                </span>
                <span className="text-gray-400">
                  {legal
                    ? "regulasi resmi (UU/PP/PMK/Permen) → default akses Publik"
                    : "dokumen pendukung → default akses Internal"}
                </span>
              </p>
            );
          })()}
          {errors.bentuk_dokumen_id && (
            <p className="mt-1 text-xs text-red-600">
              {errors.bentuk_dokumen_id.message}
            </p>
          )}
        </div>
      </div>

      {/* ── Dimensi multi-select ── */}
      <div>
        <label className="block text-sm font-medium text-gray-700 mb-2">
          Dimensi Fungsional
        </label>
        <Controller
          name="dimensi_ids"
          control={control}
          render={({ field }) => (
            <div className="grid grid-cols-2 gap-2 sm:grid-cols-4">
              {dimensiList.map((d) => (
                <label
                  key={d.id}
                  className="flex items-center gap-2 cursor-pointer"
                >
                  <input
                    type="checkbox"
                    className="rounded border-gray-300 text-blue-600"
                    checked={field.value.includes(d.id)}
                    onChange={(e) => {
                      if (e.target.checked) {
                        field.onChange([...field.value, d.id]);
                      } else {
                        field.onChange(
                          field.value.filter((id: string) => id !== d.id),
                        );
                      }
                    }}
                  />
                  <span className="text-sm text-gray-700">
                    <span className="font-mono text-xs text-gray-500">
                      {d.kode}
                    </span>{" "}
                    {d.nama}
                  </span>
                </label>
              ))}
            </div>
          )}
        />
      </div>

      {/* ── Unit Kerja searchable multi-select ── */}
      <div>
        <label className="block text-sm font-medium text-gray-700 mb-2">
          Unit Kerja
        </label>
        <Controller
          name="unit_kerja_ids"
          control={control}
          render={({ field }) => (
            <SearchableMultiSelect
              options={unitKerjaList.map((u) => ({
                value: u.id,
                label: u.nama,
                hint: u.kode,
              }))}
              value={field.value}
              onChange={field.onChange}
              placeholder="Pilih unit kerja..."
            />
          )}
        />
      </div>

      {/* ── PIC searchable multi-select ── */}
      <div>
        <label className="block text-sm font-medium text-gray-700 mb-2">
          PIC (Person in Charge)
        </label>
        <Controller
          name="pic_ids"
          control={control}
          render={({ field }) => (
            <SearchableMultiSelect
              options={picList.map((p) => ({
                value: p.id,
                label: p.jabatan ? `${p.nama} — ${p.jabatan}` : p.nama,
              }))}
              value={field.value}
              onChange={field.onChange}
              placeholder="Pilih PIC..."
            />
          )}
        />
      </div>

      {/* ── Stakeholder (cascading: parent → child optional) ── */}
      <Controller
        name="stakeholder_ids"
        control={control}
        render={({ field }) => {
          const parentStakeholders = stakeholderList.filter((s) => !s.parent_id);
          const childOptions = selectedStakeholderParentId
            ? stakeholderList.filter((s) => s.parent_id === selectedStakeholderParentId)
            : [];
          const currentChildId =
            field.value?.[0] && childOptions.some((c) => c.id === field.value[0])
              ? field.value[0]
              : "";

          return (
            <div className="grid grid-cols-1 gap-4 sm:grid-cols-2">
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-1">
                  Stakeholder
                </label>
                <select
                  value={selectedStakeholderParentId}
                  onChange={(e) => {
                    const pid = e.target.value;
                    setSelectedStakeholderParentId(pid);
                    field.onChange(pid ? [pid] : []);
                  }}
                  className="w-full rounded-lg border border-gray-300 px-3 py-2 text-sm focus:border-blue-500 focus:ring-1 focus:ring-blue-500"
                >
                  <option value="">Pilih stakeholder...</option>
                  {parentStakeholders.map((s) => (
                    <option key={s.id} value={s.id}>
                      {s.nama}
                    </option>
                  ))}
                </select>
              </div>

              <div>
                <label className="block text-sm font-medium text-gray-700 mb-1">
                  Sub-Stakeholder
                </label>
                <select
                  value={currentChildId}
                  disabled={childOptions.length === 0}
                  onChange={(e) => {
                    const cid = e.target.value;
                    field.onChange(cid ? [cid] : [selectedStakeholderParentId]);
                  }}
                  className="w-full rounded-lg border border-gray-300 px-3 py-2 text-sm focus:border-blue-500 focus:ring-1 focus:ring-blue-500 disabled:bg-gray-50 disabled:text-gray-400 disabled:cursor-not-allowed"
                >
                  <option value="">
                    {childOptions.length > 0
                      ? "Pilih sub-stakeholder (opsional)"
                      : "Pilih stakeholder terlebih dahulu"}
                  </option>
                  {childOptions.map((c) => (
                    <option key={c.id} value={c.id}>
                      {c.nama}
                    </option>
                  ))}
                </select>
              </div>
            </div>
          );
        }}
      />

      {/* ── Tingkat Kerahasiaan ── */}
      <div>
        <label className="block text-sm font-medium text-gray-700 mb-1">
          Tingkat Kerahasiaan <span className="text-red-500">*</span>
        </label>
        <select
          {...register("tingkat_kerahasiaan")}
          className="w-full rounded-lg border border-gray-300 px-3 py-2 text-sm"
        >
          <option value="PUBLIC">Publik</option>
          <option value="INTERNAL">Internal</option>
          <option value="CONFIDENTIAL">Rahasia</option>
          <option value="SECRET">Sangat Rahasia</option>
        </select>
        <p className="mt-1 text-xs text-gray-500">
          Default otomatis dari bentuk dokumen: legal/regulasi → Publik, non-legal → Internal.
          Dapat diubah manual bila perlu.
        </p>
      </div>

      {/* ── Tags ── */}
      <div>
        <label className="block text-sm font-medium text-gray-700 mb-2">
          Tag Terstruktur
        </label>
        <Controller
          name="tag_ids"
          control={control}
          render={({ field }) => (
            <SearchableMultiSelect
              options={tagList.map((t) => ({
                value: t.id,
                label: t.nama,
                hint: t.kategori,
              }))}
              value={field.value}
              onChange={field.onChange}
              placeholder="Cari dan pilih tag..."
              emptyText="Tag tidak ditemukan"
            />
          )}
        />
      </div>

      {/* ── Free Tags ── */}
      <div>
        <label className="block text-sm font-medium text-gray-700 mb-1">
          Tag Bebas
        </label>
        <div className="flex gap-2">
          <input
            value={freeTagInput}
            onChange={(e) => setFreeTagInput(e.target.value)}
            onKeyDown={(e) => {
              if (e.key === "Enter") {
                e.preventDefault();
                addFreeTag();
              }
            }}
            className="flex-1 rounded-lg border border-gray-300 px-3 py-2 text-sm"
            placeholder="Ketik tag dan tekan Enter..."
          />
          <button
            type="button"
            onClick={addFreeTag}
            className="rounded-lg bg-gray-100 px-4 py-2 text-sm font-medium text-gray-700 hover:bg-gray-200"
          >
            Tambah
          </button>
        </div>
        {freeTags.length > 0 && (
          <div className="mt-2 flex flex-wrap gap-2">
            {freeTags.map((tag) => (
              <span
                key={tag}
                className="flex items-center gap-1 rounded-full bg-blue-100 px-3 py-1 text-xs text-blue-800"
              >
                <TagIcon className="h-3 w-3" />
                {tag}
                <button
                  type="button"
                  onClick={() => removeFreeTag(tag)}
                  className="ml-1 hover:text-blue-600"
                >
                  <X className="h-3 w-3" />
                </button>
              </span>
            ))}
          </div>
        )}
      </div>

      {/* ── Dates Row ── */}
      <div className="grid grid-cols-1 gap-4 sm:grid-cols-3">
        <div>
          <label className="block text-sm font-medium text-gray-700 mb-1">
            Tanggal Dokumen
          </label>
          <input
            type="date"
            {...register("tanggal_dokumen")}
            className="w-full rounded-lg border border-gray-300 px-3 py-2 text-sm"
          />
        </div>
        <div>
          <label className="block text-sm font-medium text-gray-700 mb-1">
            Tanggal Berlaku
          </label>
          <input
            type="date"
            {...register("tanggal_berlaku")}
            className="w-full rounded-lg border border-gray-300 px-3 py-2 text-sm"
          />
        </div>
        <div>
          <label className="block text-sm font-medium text-gray-700 mb-1">
            Tanggal Berakhir
          </label>
          <input
            type="date"
            {...register("tanggal_berakhir")}
            className="w-full rounded-lg border border-gray-300 px-3 py-2 text-sm"
          />
        </div>
      </div>

      {/* ── Deskripsi ── */}
      <div>
        <div className="flex items-center justify-between mb-1">
          <label className="block text-sm font-medium text-gray-700">
            Deskripsi
          </label>
          <button
            type="button"
            onClick={() => setShowDeskripsiModal(true)}
            disabled={!watchedDeskripsi?.trim()}
            title="Baca deskripsi lengkap"
            className="inline-flex items-center gap-1 text-xs font-medium text-blue-600 hover:text-blue-700 disabled:text-gray-300 disabled:cursor-not-allowed"
          >
            <BookOpen className="h-3.5 w-3.5" />
            Baca
          </button>
        </div>
        <textarea
          {...register("deskripsi")}
          rows={3}
          className="w-full rounded-lg border border-gray-300 px-3 py-2 text-sm"
          placeholder="Deskripsi singkat dokumen..."
        />
      </div>

      {/* ── Actions ── */}
      <div className="flex justify-end gap-3 pt-2">
        {onCancel && (
          <button
            type="button"
            onClick={onCancel}
            className="rounded-lg border border-gray-300 px-5 py-2 text-sm font-medium text-gray-700 hover:bg-gray-50"
          >
            Batal
          </button>
        )}
        <button
          type="submit"
          disabled={isSubmitting || createMutation.isPending}
          className="rounded-lg bg-blue-600 px-5 py-2 text-sm font-medium text-white hover:bg-blue-700 disabled:opacity-50"
        >
          {isSubmitting || createMutation.isPending
            ? "Menyimpan..."
            : "Simpan Dokumen"}
        </button>
      </div>

      {/* ── Modal: detail deskripsi ── */}
      {showDeskripsiModal && (
        <div
          className="fixed inset-0 z-50 flex items-center justify-center bg-black/40 p-4"
          onClick={() => setShowDeskripsiModal(false)}
        >
          <div
            className="flex max-h-[80vh] w-full max-w-2xl flex-col rounded-xl bg-white shadow-xl"
            onClick={(e) => e.stopPropagation()}
          >
            <div className="flex items-center justify-between border-b border-gray-100 px-5 py-3">
              <h3 className="flex items-center gap-2 text-sm font-semibold text-gray-800">
                <BookOpen className="h-4 w-4 text-blue-600" />
                Detail Deskripsi Dokumen
              </h3>
              <button
                type="button"
                onClick={() => setShowDeskripsiModal(false)}
                className="rounded p-1 text-gray-400 hover:bg-gray-100 hover:text-gray-600"
                aria-label="Tutup"
              >
                <X className="h-4 w-4" />
              </button>
            </div>
            <div className="overflow-y-auto px-5 py-4">
              <p className="whitespace-pre-wrap text-sm leading-relaxed text-gray-700">
                {watchedDeskripsi?.trim() || "Belum ada deskripsi."}
              </p>
            </div>
          </div>
        </div>
      )}

      {/* Delete duplicate confirmation */}
      <ConfirmModal
        open={dupDeleteTarget !== null}
        variant="danger"
        title="Hapus Dokumen Duplikat"
        message={`Yakin ingin menghapus "${dupDeleteTarget?.judul ?? ""}"? Dokumen akan dihapus dari daftar.`}
        confirmLabel="Hapus"
        cancelLabel="Batal"
        loading={deleteDuplicateMutation.isPending}
        onConfirm={() => {
          if (dupDeleteTarget) deleteDuplicateMutation.mutate(dupDeleteTarget.id);
        }}
        onCancel={() => setDupDeleteTarget(null)}
      />
    </form>
  );
}
